import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { FontAwesomeIconProps } from '#src/atoms/icons/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { HeadlineS, LabelS } from '#src/atoms/typography/index.tsx';
import { Row } from '#src/components/seo/ui.tsx';

import type { Props as TransferProps } from './Transfer/index.tsx';
import { Transfer } from './Transfer/index.tsx';

const SectionTitle = styled(LabelS).attrs({
  as: 'p',
  bold: true,
  uppercase: true,
})`
  white-space: nowrap;
`;

type Props<T extends string> = {
  stats: TransferProps<T>['stats'];
  icons: Record<T, FontAwesomeIconProps['icon']>;
  nationalTeamMemberships: TransferProps<T>['transfer'][];
  clubMemberships: TransferProps<T>['transfer'][];
};

export const CareerPath = <T extends string>({
  stats,
  icons,
  nationalTeamMemberships,
  clubMemberships,
}: Props<T>) => {
  return (
    <Vertical gap={2}>
      <HeadlineS as="h2">
        <FormattedMessage
          id="Seo.Transfers.careerPath"
          defaultMessage="Career"
        />
      </HeadlineS>

      <Vertical gap={3}>
        <Vertical>
          <Row
            style={{
              gridTemplateColumns: `var(--triple-unit) 1fr repeat(${stats.length}, var(--quadruple-unit))`,
            }}
          >
            <SectionTitle color="var(--c-nd-600)">
              <FormattedMessage id="Seo.Transfers.club" defaultMessage="Club" />
            </SectionTitle>
            <span />
            {stats.map(stat => (
              <LabelS as="p" key={stat} color="var(--c-nd-600)">
                <FontAwesomeIcon icon={icons[stat]} />
              </LabelS>
            ))}
          </Row>
          {clubMemberships
            .sort((a, b) => b.startDate.getTime() - a.startDate.getTime())
            .map(transfer => (
              <Transfer key={transfer.id} transfer={transfer} stats={stats} />
            ))}
        </Vertical>
        {!!nationalTeamMemberships.length && (
          <Vertical>
            <Row
              style={{
                gridTemplateColumns: `var(--triple-unit) 1fr repeat(${stats.length}, var(--quadruple-unit))`,
              }}
            >
              <SectionTitle color="var(--c-nd-600)">
                <FormattedMessage
                  id="Seo.Transfers.nationalTeam"
                  defaultMessage="National Team"
                />
              </SectionTitle>
              <span />
            </Row>
            {nationalTeamMemberships
              .sort((a, b) => b.startDate.getTime() - a.startDate.getTime())
              .map(transfer => (
                <Transfer key={transfer.id} transfer={transfer} stats={stats} />
              ))}
          </Vertical>
        )}
      </Vertical>
    </Vertical>
  );
};
